<template>
  <div class="gemx-control">
    <slot name="label"></slot>
    <div class="gemx-control gemx-control-input-number">
      <div class="gemx-control-input-number_container">
        <input
          v-model="val"
          type="number"
          class="rounded-medium caret-primary-300 text-12 text-dark-high placeholder:text-dark-disabled focus:border-primary-300 disabled:border-dark-200 disabled:text-dark-disabled bg-dark-400 hover:bg-dark-200 h-32 w-full border border-transparent px-8 outline-none transition duration-200 hover:border-transparent disabled:cursor-not-allowed"
          :placeholder="placeholder"
          :disabled="readonly"
          :class="[inputClass]"
          @input="onChange"
          @change="change"
          @blur="blur" />
      </div>
    </div>
    <slot name="info"></slot>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue';

type PropsType = {
  id?: string;
  value?: number;
  placeholder?: string;
  readonly?: boolean;
  min?: number;
  max?: number;
  inputClass?: string;
  controlChange?: (id: string, value?: any) => void;
};

const props = withDefaults(defineProps<PropsType>(), {
  id: '',
  min: Number.MIN_SAFE_INTEGER,
  max: Number.MAX_SAFE_INTEGER,
  readonly: false,
  placeholder: '',
});

const emit = defineEmits<{
  (e: 'controlOnChange', controlId?: string | number, value?: any): void;
  // (e: 'controlChange', controlId?: string | number, value?: any): void;
}>();

const val = ref<number>(props.value ?? 0);

watch(
  () => props.value,
  (v) => {
    if (v !== undefined) {
      val.value = v;
    }
  },
);

const interval = ref<any>();
const timeout = ref<any>();

watch(
  () => props.value,
  (newPropValue) => {
    val.value = newPropValue || 0;
  },
);

const checkRangeValue = (value: number): number => {
  if (value > props.max) return props.max;
  if (value < props.min) return props.min;
  return value;
};

const controlPress = (change: any) => {
  let value = parseFloat(`${val.value}`) || 0;
  value += change;
  val.value = checkRangeValue(value);
  onChange();
};

const doChange = (changing: any) => {
  timeout.value = setTimeout(() => {
    interval.value = setInterval(() => {
      controlPress(changing);
    }, 100);
  }, 500);
};

const controlRelease = () => {
  clearInterval(interval.value);
  clearTimeout(timeout.value);
  interval.value = null;
  timeout.value = null;
  change();
};
const controlClick = (change: any) => {
  val.value += change;
  val.value = checkRangeValue(val.value);
  props.controlChange?.(props.id, val.value);
  // emit('controlChange', props.id, val.value);
};
const onChange = () => {
  if (val.value > props.max || props.min > val.value) {
    return;
  }
  emit('controlOnChange', props.id, checkRangeValue(val.value));
};
const change = () => {
  if (val.value > props.max || props.min > val.value) {
    return;
  }
  props.controlChange?.(props.id, checkRangeValue(val.value));
  // emit('controlChange', props.id, checkRangeValue(val.value));
};
const blur = () => {
  val.value = checkRangeValue(val.value);
};
</script>

<style lang="scss" scoped>
$background: #242424;
$backgroundHover: #424242;
$text: #e2e2e2;
$textShade2: #7f7f7f;
$textShade3: #dadada;

$blue: #839bc0;

.gemx-control-input-number {
  ::-webkit-input-placeholder,
  ::-moz-placeholder,
  :-moz-placeholder,
  :-ms-input-placeholder {
    /* Chrome/Opera/Safari */
    color: $textShade2;
  }

  &:hover {
    .gemx-control-input-number_control {
      &_inc,
      &_dec {
        transition: all 0.3s;
        border-color: #85929e;
      }
    }
  }

  .gemx-control-input-number_container {
    position: relative;
  }

  input[type='number'] {
    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      -webkit-appearance: none;
    }
  }

  button[disabled] {
    border: none;
    background-color: #f0f3f6;

    svg path {
      fill: #c5cbd7;
    }

    &:hover {
      cursor: not-allowed;
      background-color: #f0f3f6;

      svg path {
        fill: #c5cbd7;
      }
    }
  }

  .gemx-control-input-number_control {
    position: absolute;
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-orient: vertical;
    -webkit-box-direction: normal;
    -ms-flex-direction: column;
    flex-direction: column;
    -webkit-box-pack: justify;
    -ms-flex-pack: justify;
    justify-content: space-between;
    top: 0;
    bottom: 0;
    right: 0;
    &_inc,
    &_dec {
      -webkit-box-flex: 1 1 auto;
      -ms-flex-positive: 1;
      flex-grow: 1;
      outline: none;
      cursor: pointer;
      display: -webkit-box;
      display: -ms-flexbox;
      display: flex;
      -webkit-box-pack: center;
      -ms-flex-pack: center;
      justify-content: center;
      -webkit-box-align: center;
      -ms-flex-align: center;
      align-items: center;
      -webkit-transition: linear 0.3s;
      transition: linear 0.3s;
      width: 20px;
      height: 16px;

      svg path {
        -webkit-transition: linear 0.3s;
        transition: linear 0.3s;
        fill: #a6a6a6;
      }
      &:hover {
        background-color: #525252;
        svg path {
          fill: #e2e2e2;
        }
      }
    }
    &_inc {
      border-top-right-radius: 3px;
      border-bottom: none;
    }
    &_dec {
      border-bottom-right-radius: 3px;
    }
  }
}
</style>
